export const normalizeAiAnalysis = (aiAnalysis: unknown): Record<string, any> | null => {
    if(!aiAnalysis) {
        return null
    }

    if(typeof aiAnalysis === "string") {
        try {
            return normalizeAiAnalysis(JSON.parse(aiAnalysis))
        } catch (error) {
            return null
        }
    }

    if(Array.isArray(aiAnalysis)) {
        return normalizeAiAnalysis(aiAnalysis[0])
    }

    if(typeof aiAnalysis === "object") {
        return aiAnalysis as Record<string, any>
    }

    return null
}

export const getAiAnalysisValue = (analysis: Record<string, any> | null, path: string): unknown => {
    if(!analysis) {
        return undefined
    }

    return path.split(".").reduce((current, key) => {
        if(current && typeof current === "object" && key in (current as Record<string, any>)) {
            return (current as Record<string, any>)[key]
        }

        return undefined
    }, analysis as unknown)
}

const parseNumericValue = (value: unknown): number | null => {
    if(typeof value === "number") {
        return Number.isNaN(value) ? null : value
    }

    if(typeof value !== "string") {
        return null
    }

    const raw = value.trim()
    if(!raw) {
        return null
    }

    let normalized = raw.replace(/[^\d,.-]/g, "")
    if(!normalized) {
        return null
    }

    const commaCount = (normalized.match(/,/g) ?? []).length
    const dotCount = (normalized.match(/\./g) ?? []).length

    if(commaCount > 0 && dotCount > 0) {
        const lastComma = normalized.lastIndexOf(",")
        const lastDot = normalized.lastIndexOf(".")
        if(lastComma > lastDot) {
            normalized = normalized.replace(/\./g, "").replace(",", ".")
        } else {
            normalized = normalized.replace(/,/g, "")
        }
    } else if(commaCount > 0) {
        if(/^-?\d{1,3}(,\d{3})+$/.test(normalized)) {
            normalized = normalized.replace(/,/g, "")
        } else {
            normalized = normalized.replace(",", ".")
        }
    } else if(dotCount > 0 && /^-?\d{1,3}(\.\d{3})+$/.test(normalized)) {
        normalized = normalized.replace(/\./g, "")
    }

    const parsed = Number(normalized)
    return Number.isNaN(parsed) ? null : parsed
}

export const getAiAnalysisNumber = (
    analysis: Record<string, any> | null,
    paths: string[],
    fallback: number | null
): number | null => {
    for(const path of paths) {
        const value = getAiAnalysisValue(analysis, path)
        if(value === null || value === undefined || value === "") {
            continue
        }

        const numericValue = parseNumericValue(value)
        if(numericValue !== null) {
            return numericValue
        }
    }

    return fallback ?? null
}
